import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  output,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  EstabelecimentoDataService,
  EstabelecimentoService,
  ProfissionalDataService,
  ProfissionalService,
  ServicoDataService,
  TipoServicoDataService,
  NichoTermosService,
} from '@nomoh-monorepo/data-access-bardo-administrador';

import {
  DeviceService,
  FormFieldComponent,
  NFieldsetComponent,
  NMultiselectComponent,
  PhoneInputComponent,
} from '@nomoh-monorepo/shared-common-ui';
import {
  DiasSemanaOptions,
  IProfissional,
  IProfissionalDiasPresente,
  IProfissionalServico,
} from '@nomoh-monorepo/shared-types';
import {
  expedienteObrigatorio,
  prestaAtendimentoEditavel,
  prestaAtendimentoSugerido,
  servicosObrigatorios,
} from '@nomoh-monorepo/bardo-administrador-util';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import {
  mascararEmail,
  mascararTelefone,
  telefoneValidator,
} from '@nomoh-monorepo/shared-util-tools';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { PasswordModule } from 'primeng/password';
import { SelectModule } from 'primeng/select';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { I18nService } from '@nomoh-monorepo/data-access-shared-i18n';
@Component({
  selector: 'lib-profissional-create-edit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    PhoneInputComponent,
    MessageModule,
    MultiSelectModule,
    PasswordModule,
    SelectModule,
    ToggleButtonModule,
    FormFieldComponent,
    NFieldsetComponent,
    NMultiselectComponent,
    TranslateModule,
  ],
  templateUrl: './profissional-create-edit.component.html',
  styleUrls: ['./profissional-create-edit.component.scss'],
})
export class ProfissionalCreateEditComponent
  implements AfterViewInit, OnChanges, OnDestroy, OnInit
{
  //#region input e output
  closed = output<void>();
  @Input() profissional: IProfissional | null = null;
  @Input() resetKey = 0;

  @ViewChild('footerTemplate', { static: false })
  private _footerTemplateRef!: TemplateRef<unknown>;

  @Output() footerTemplateAction = new EventEmitter<TemplateRef<unknown>>();
  @Output() footerReset = new EventEmitter<void>();
  //#endregion
  //#region Injeção de Dependências
  private fb = inject(FormBuilder);
  private profissionalService = inject(ProfissionalService);
  private estabelecimentoService = inject(EstabelecimentoService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private profissionalDataService = inject(ProfissionalDataService);
  private tipoServicoDataService = inject(TipoServicoDataService);
  private notificacaoService = inject(NotificacaoService);
  private translateService = inject(TranslateService);
  private i18nService = inject(I18nService);
  private langChangeTrigger = toSignal(this.translateService.onLangChange);
  servicoDataService = inject(ServicoDataService);
  deviceService = inject(DeviceService);
  $nicho = inject(NichoTermosService).$nicho;

  //#endregion

  //#region Signals
  $dropdownAppendTo = computed(() =>
    this.deviceService.$isMobile() ? undefined : 'body'
  );

  $estabelecimentoAtual = this.estabelecimentoDataService.$estabelecimentoAtual;
  salvando = signal(false);
  habilitarAcessoAtivo = signal(false);
  salvandoAcesso = signal(false);
  profissionalRecemCriado = signal(false);
  acessoConcedido = signal(false);

  formAcesso: FormGroup = this.fb.group({
    email:    ['', [Validators.required, Validators.email]],
    telefone: ['', [Validators.required, telefoneValidator()]],
    senha:    ['', Validators.required],
  });

  get temAcessoAtivo(): boolean {
    return !!this.profissional?.pessoaId || this.acessoConcedido();
  }

  get emailMascarado(): string | null {
    return this.profissional?.email ? mascararEmail(this.profissional.email) : null;
  }

  get telefoneMascarado(): string | null {
    return this.profissional?.telefone
      ? mascararTelefone(this.profissional.telefone)
      : null;
  }

  get habilitarAcessoAtivoValue(): boolean {
    return this.habilitarAcessoAtivo();
  }
  set habilitarAcessoAtivoValue(value: boolean) {
    this.habilitarAcessoAtivo.set(value);
  }
  $diasDisponiveis = computed(() => {
    this.langChangeTrigger();
    this.i18nService.idioma();
    const estabelecimento = this.$estabelecimentoAtual();

    if (!estabelecimento?.diasFuncionamento?.length) {
      // Se não houver dias configurados, retornar array vazio
      return [];
    }

    const diasFuncionamentoIds = estabelecimento.diasFuncionamento.map(
      (d) => d.diaId
    );
    const nomesKeys = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];
    const todosDias = DiasSemanaOptions.map((dia) => ({
      id: dia.id,
      label: this.translateService.instant(`BARDO_PROFISSIONAIS.DIAS.${nomesKeys[dia.id - 1]}`),
    }));

    // Filtrar apenas dias em que estabelecimento funciona
    return todosDias.filter((dia) => diasFuncionamentoIds.includes(dia.id));
  });

  private $quotas = signal<Awaited<ReturnType<EstabelecimentoService['getPlanoQuotas']>> | null>(null);

  $perfisDisponiveis = computed(() => {
    this.langChangeTrigger();
    this.i18nService.idioma();
    const quotas = this.$quotas();
    const perfilAtual = this.form?.get('perfilAcesso')?.value as string | null;

    return this.perfisAcesso.filter((p) => {
      if (!quotas) return true;
      if (p.value === perfilAtual) return true;
      if (p.value === 'RECEPCAO') return !!quotas.allowRecepcao;
      if (p.value === 'ADMINISTRADOR') return !!quotas.allowAdminExtra;
      return true;
    });
  });

  //#endregion

  //#region ViewChild para templates
  @ViewChild('headerTemplate', { static: false })
  headerTemplate!: TemplateRef<any>;

  @ViewChild('footerTemplate', { static: false })
  footerTemplate!: TemplateRef<any>;
  //#endregion

  get perfisAcesso(): { label: string; value: string }[] {
    return [
      { label: this.translateService.instant('BARDO_PROFISSIONAIS.PERFIS.ADMINISTRADOR'), value: 'ADMINISTRADOR' },
      { label: this.translateService.instant('BARDO_PROFISSIONAIS.PERFIS.RECEPCAO'), value: 'RECEPCAO' },
      { label: this.translateService.instant('BARDO_PROFISSIONAIS.PERFIS.PROFISSIONAL'), value: 'PROFISSIONAL' },
    ];
  }

  $avisoQuota = signal<string | null>(null);

  //#region propriedades
  form: FormGroup = this.fb.group({
    nome: [
      '',
      [Validators.required, Validators.minLength(3), Validators.maxLength(50)],
    ],
    descricao: ['', [Validators.maxLength(100)]],
    perfilAcesso: ['', Validators.required],
    prestaAtendimento: [true],
    diasSelecionados: [[]],
    ferias: [false],
    inicioExpediente: [''],
    fimExpediente: [{ value: '', disabled: true }],
    almocoInicio: [''],
    almocoFim: [{ value: '', disabled: true }],
    servicosSelecionados: [[]],
  });

  /**
   * Serviços/expediente são obrigatórios para PROFISSIONAL/ADMINISTRADOR;
   * opcionais para RECEPCAO sem serviço vinculado (aprovado em 2026-07-10 —
   * docs/perfil/REFINAMENTO_SERVICOS_OPCIONAIS_RECEPCAO.md).
   */
  $servicosObrigatorios = signal(true);
  $expedienteObrigatorio = signal(true);
  $servicosHelpText = signal('');
  $expedienteHelpText = signal('');

  horariosInicio: { label: string; value: string }[] = [];
  horariosFim: { label: string; value: string }[] = [];
  horariosAlmocoInicio: { label: string; value: string }[] = [];
  horariosAlmocoFim: { label: string; value: string }[] = [];
  //#endregion

  constructor() {
    effect(() => {
      if (this.profissional?.id) {
        const profissional = this.profissionalDataService
          .$profissionais()
          .find((f) => f.id == this.profissional!.id);

        if (profissional) {
          this.profissional = profissional;
          this.carregarProfissional();
        }
      }
    });

    effect(() => {
      this.$estabelecimentoAtual();
      this.gerarHorariosDisponiveis();
      const inicio = this.form?.get('inicioExpediente')?.value;
      if (inicio) {
        this.gerarHorariosFim(inicio);
        this.gerarHorariosAlmocoInicio();
        const almocoInicio = this.form?.get('almocoInicio')?.value;
        if (almocoInicio) {
          this.gerarHorariosAlmocoFim(almocoInicio);
        }
      }
    });
  }
  ngAfterViewInit(): void {
    setTimeout(() => {
      this.footerTemplateAction.emit(this._footerTemplateRef);
    });
  }

  ngOnDestroy(): void {
    this.footerReset.emit();
  }

  ngOnInit(): void {
    this.gerarHorariosDisponiveis();
    this.carregarQuotasPlano();
    this.form.get('perfilAcesso')?.valueChanges.subscribe(() => {
      this.atualizarControlePrestaAtendimento();
      this.atualizarValidadoresPorPerfil();
    });
    this.form.get('prestaAtendimento')?.valueChanges.subscribe(() => {
      this.atualizarValidadoresPorPerfil();
    });
    this.form.get('servicosSelecionados')?.valueChanges.subscribe(() => {
      this.atualizarValidadoresPorPerfil();
    });
    this.atualizarControlePrestaAtendimento();
    this.atualizarValidadoresPorPerfil();
    this.form.get('inicioExpediente')?.valueChanges.subscribe((inicio) => {
      this.gerarHorariosFim(inicio);
      const fimExpedienteCtrl = this.form.get('fimExpediente');
      if (inicio) {
        fimExpedienteCtrl?.enable({ emitEvent: false });
      } else {
        fimExpedienteCtrl?.disable({ emitEvent: false });
      }
    });
    this.form.get('fimExpediente')?.valueChanges.subscribe(() => {
      this.gerarHorariosAlmocoInicio();
      const almocoInicio = this.form.get('almocoInicio')?.value;
      if (almocoInicio) {
        this.gerarHorariosAlmocoFim(almocoInicio);
      }
    });
    this.form.get('almocoInicio')?.valueChanges.subscribe((inicio) => {
      this.gerarHorariosAlmocoFim(inicio);
      const almocoFimCtrl = this.form.get('almocoFim');
      if (inicio) {
        almocoFimCtrl?.enable({ emitEvent: false });
      } else {
        almocoFimCtrl?.disable({ emitEvent: false });
      }
    });
  }

  private async carregarQuotasPlano(): Promise<void> {
    try {
      const quotas = await this.estabelecimentoService.getPlanoQuotas();
      this.$quotas.set(quotas);

      const bloqueados: string[] = [];
      if (!quotas.allowRecepcao) bloqueados.push('Recepção');
      if (!quotas.allowAdminExtra) bloqueados.push('Administrador extra');
      const avisos: string[] = [];
      if (bloqueados.length) {
        avisos.push(
          `Plano ${quotas.nome ?? ''}: ${bloqueados.join(
            ' e '
          )} indisponível(is) neste plano.`
        );
      }
      if (quotas.maxProfissionais != null) {
        avisos.push(
          `Limite de ${quotas.maxProfissionais} profissional(is) ativo(s).`
        );
      }
      this.$avisoQuota.set(avisos.length ? avisos.join(' ') : null);
    } catch {
      this.$quotas.set(null);
      this.$avisoQuota.set(null);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['resetKey'] && !changes['resetKey'].firstChange) {
      this.resetarFormulario(true);
      return;
    }
    if (changes['profissional']) {
      this.prepararFormulario();
    }
  }

  private prepararFormulario() {
    if (this.profissional?.id) {
      this.carregarProfissional();
      return;
    }

    this.resetarFormulario();
  }

  private resetarFormulario(resetHorarios = false) {
    this.profissional = null;
    this.profissionalRecemCriado.set(false);
    this.habilitarAcessoAtivo.set(false);
    this.acessoConcedido.set(false);
    this.formAcesso.reset();

    this.form.reset({
      nome: '',
      descricao: '',
      perfilAcesso: '',
      prestaAtendimento: true,
      diasSelecionados: [],
      ferias: false,
      inicioExpediente: '',
      fimExpediente: { value: '', disabled: true },
      almocoInicio: '',
      almocoFim: { value: '', disabled: true },
      servicosSelecionados: [],
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();

    if (resetHorarios) {
      this.horariosInicio = [];
      this.horariosFim = [];
      this.horariosAlmocoInicio = [];
      this.horariosAlmocoFim = [];
    }
  }

  gerarHorariosDisponiveis() {
    const estabelecimento = this.$estabelecimentoAtual();
    const horaAbertura = estabelecimento?.horaAbertura || '08:00';
    const horaFechamento = estabelecimento?.horaFechamento || '18:00';

    const inicio = this.parseHorario(horaAbertura);
    const fim = this.parseHorario(horaFechamento);

    this.horariosInicio = [];
    let current = inicio;

    while (current < fim) {
      const horarioStr = this.formatarHorario(current);
      this.horariosInicio.push({ label: horarioStr, value: horarioStr });
      current += 30; // Incremento de 30 minutos
    }

    this.horariosFim = [];
  }

  gerarHorariosAlmocoInicio() {
    const inicioExpediente = this.form.get('inicioExpediente')?.value;
    const fimExpediente = this.form.get('fimExpediente')?.value;

    if (!inicioExpediente || !fimExpediente) {
      this.horariosAlmocoInicio = [];
      this.form.patchValue(
        { almocoInicio: '', almocoFim: '' },
        { emitEvent: false }
      );
      this.horariosAlmocoFim = [];
      this.form.get('almocoFim')?.disable({ emitEvent: false });
      return;
    }

    const inicio = this.parseHorario(inicioExpediente);
    const fim = this.parseHorario(fimExpediente);

    this.horariosAlmocoInicio = [];
    let current = inicio;

    while (current <= fim - 30) {
      const horarioStr = this.formatarHorario(current);
      this.horariosAlmocoInicio.push({ label: horarioStr, value: horarioStr });
      current += 30;
    }

    const almocoInicioAtual = this.form.get('almocoInicio')?.value;
    if (
      almocoInicioAtual &&
      !this.horariosAlmocoInicio.some((h) => h.value === almocoInicioAtual)
    ) {
      this.form.patchValue(
        { almocoInicio: '', almocoFim: '' },
        { emitEvent: false }
      );
      this.horariosAlmocoFim = [];
      this.form.get('almocoFim')?.disable({ emitEvent: false });
    }
  }

  gerarHorariosAlmocoFim(inicioSelecionado: string) {
    if (!inicioSelecionado) {
      this.horariosAlmocoFim = [];
      this.form.patchValue({ almocoFim: '' }, { emitEvent: false });
      this.form.get('almocoFim')?.disable({ emitEvent: false });
      return;
    }

    const fimExpediente = this.form.get('fimExpediente')?.value;
    if (!fimExpediente) {
      this.horariosAlmocoFim = [];
      return;
    }

    const inicio = this.parseHorario(inicioSelecionado);
    const fim = this.parseHorario(fimExpediente);

    this.horariosAlmocoFim = [];
    let current = inicio + 30;

    while (current <= fim) {
      const horarioStr = this.formatarHorario(current);
      this.horariosAlmocoFim.push({ label: horarioStr, value: horarioStr });
      current += 30;
    }

    const fimAtual = this.form.get('almocoFim')?.value;
    if (fimAtual && this.parseHorario(fimAtual) <= inicio) {
      this.form.patchValue({ almocoFim: '' }, { emitEvent: false });
    }
  }

  gerarHorariosFim(inicioSelecionado: string) {
    if (!inicioSelecionado) {
      this.horariosFim = [];
      this.form.patchValue({ fimExpediente: '' }, { emitEvent: false });
      return;
    }

    const estabelecimento = this.$estabelecimentoAtual();
    if (!estabelecimento?.horaFechamento) {
      this.horariosFim = [];
      return;
    }

    const inicio = this.parseHorario(inicioSelecionado);
    const fim = this.parseHorario(estabelecimento.horaFechamento);

    this.horariosFim = [];
    let current = inicio + 30; // mínimo 30min após o início

    while (current <= fim) {
      const horarioStr = this.formatarHorario(current);
      this.horariosFim.push({ label: horarioStr, value: horarioStr });
      current += 30;
    }

    const fimAtual = this.form.get('fimExpediente')?.value;
    if (fimAtual && this.parseHorario(fimAtual) <= inicio) {
      this.form.patchValue({ fimExpediente: '' }, { emitEvent: false });
    }

    const almocoInicio = this.form.get('almocoInicio')?.value;
    if (almocoInicio) {
      this.gerarHorariosAlmocoFim(almocoInicio);
    }
  }

  /**
   * PROFISSIONAL: trava o controle em true (decisão #1, 2026-07-16). Demais
   * perfis: libera o controle e, apenas para cadastro novo e enquanto o
   * usuário não tiver mexido manualmente no toggle (control.pristine),
   * aplica a sugestão de valor por perfil (decisão #2). Não sobrescreve
   * edição de profissional existente nem um valor já escolhido pelo usuário.
   */
  private atualizarControlePrestaAtendimento(): void {
    const perfilAcesso = this.form.get('perfilAcesso')?.value;
    const control = this.form.get('prestaAtendimento');

    if (!prestaAtendimentoEditavel(perfilAcesso)) {
      control?.setValue(true, { emitEvent: false });
      control?.disable({ emitEvent: false });
      return;
    }

    control?.enable({ emitEvent: false });
    if (!this.profissional?.id && control?.pristine) {
      control.setValue(prestaAtendimentoSugerido(perfilAcesso), {
        emitEvent: false,
      });
    }
  }

  /**
   * Reaplica os validadores de servicosSelecionados/expediente conforme
   * prestaAtendimento e os serviços selecionados no momento (Opção B —
   * docs/perfil/PROPOSTA_FLAG_PRESTA_ATENDIMENTO.md — não limpa valores já
   * selecionados ao trocar o perfil/toggle).
   */
  private atualizarValidadoresPorPerfil(): void {
    const prestaAtendimento = this.form.get('prestaAtendimento')?.value;
    const servicosSelecionados = this.form.get('servicosSelecionados')?.value;

    const precisaServicos = servicosObrigatorios(prestaAtendimento);
    this.$servicosObrigatorios.set(precisaServicos);
    this.aplicarValidadorRequired('servicosSelecionados', precisaServicos);

    const precisaExpediente = expedienteObrigatorio(
      prestaAtendimento,
      servicosSelecionados
    );
    this.$expedienteObrigatorio.set(precisaExpediente);
    this.aplicarValidadorRequired('diasSelecionados', precisaExpediente);
    this.aplicarValidadorRequired('inicioExpediente', precisaExpediente);
    this.aplicarValidadorRequired('fimExpediente', precisaExpediente);

    if (precisaServicos) {
      this.$servicosHelpText.set('');
    } else if (servicosSelecionados?.length) {
      this.$servicosHelpText.set(
        'Esta pessoa também poderá ser agendada nos serviços selecionados e poderá consultar o próprio fechamento.'
      );
    } else {
      this.$servicosHelpText.set(
        'Recepção sem serviços não aparecerá como opção de atendimento para o cliente (painel e WhatsApp).'
      );
    }

    this.$expedienteHelpText.set(
      precisaExpediente ? '' : 'Opcional para recepção que não realiza atendimentos.'
    );
  }

  private aplicarValidadorRequired(controlName: string, required: boolean): void {
    const control = this.form.get(controlName);
    control?.setValidators(required ? Validators.required : null);
    control?.updateValueAndValidity({ emitEvent: false });
  }

  parseHorario(horario: string): number {
    const [h, m] = horario.split(':').map(Number);
    return h * 60 + m;
  }

  formatarHorario(minutos: number): string {
    const h = Math.floor(minutos / 60);
    const m = minutos % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }

  carregarProfissional() {
    const prof = this.profissional;
    if (!prof?.id) return;

    const diasIds = prof.diasSemanaPresente?.map((d) => d.diaId) || [];

    const inicioNormalizado = this.normalizarHorario(prof.inicioExpediente);
    const fimNormalizado = this.normalizarHorario(prof.fimExpediente);
    const almocoInicioNormalizado = this.normalizarHorario(prof.almocoInicio);
    const almocoFimNormalizado = this.normalizarHorario(prof.almocoFim);

    this.form.patchValue({
      nome: prof.nome,
      descricao: prof.descricao || '',
      perfilAcesso: prof.perfilAcesso || '',
      prestaAtendimento: prof.prestaAtendimento ?? true,
      diasSelecionados: diasIds,
      ferias: prof.ferias || false,
      inicioExpediente: inicioNormalizado,
      fimExpediente: fimNormalizado,
      almocoInicio: almocoInicioNormalizado,
      almocoFim: almocoFimNormalizado,
      servicosSelecionados: prof.servicos?.map((s) => s.servicoId) || [],
    });
    this.atualizarControlePrestaAtendimento();

    this.gerarHorariosFim(inicioNormalizado);
    this.gerarHorariosAlmocoInicio();
    if (almocoInicioNormalizado) {
      this.gerarHorariosAlmocoFim(almocoInicioNormalizado);
    }
  }

  onTelefoneAcessoChange(value: string): void {
    const ctrl = this.formAcesso.get('telefone');
    ctrl?.setValue(value);
    ctrl?.markAsDirty();
    ctrl?.markAsTouched();
  }

  async salvarAcesso(): Promise<void> {
    if (this.formAcesso.invalid || !this.profissional?.id) return;
    this.salvandoAcesso.set(true);
    try {
      const { email, telefone, senha } = this.formAcesso.getRawValue();
      await this.profissionalService.habilitarAcesso(
        this.profissional.id,
        email,
        telefone,
        senha
      );
      this.acessoConcedido.set(true);
      this.habilitarAcessoAtivo.set(false);
      this.formAcesso.reset();
    } catch {
      // Notificação de erro já é exibida pelo interceptor HTTP global.
    } finally {
      this.salvandoAcesso.set(false);
    }
  }

  async salvar() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const almocoInicio = this.form.get('almocoInicio')?.value;
    const almocoFim = this.form.get('almocoFim')?.value;
    const inicioExpediente = this.form.get('inicioExpediente')?.value;
    const fimExpediente = this.form.get('fimExpediente')?.value;

    if (
      !this.isAlmocoValido(
        inicioExpediente,
        fimExpediente,
        almocoInicio,
        almocoFim
      )
    ) {
      this.notificacaoService.notificacaoWarn(
        this.translateService.instant('BARDO_PROFISSIONAIS.FORM.INTERVALO_ALMOCO_INVALIDO')
      );
      return;
    }

    const estabelecimentoId = this.$estabelecimentoAtual()?.id;
    if (!estabelecimentoId) {
      this.notificacaoService.notificacaoWarn(
        this.translateService.instant('BARDO_PROFISSIONAIS.FORM.ESTABELECIMENTO_NAO_ENCONTRADO')
      );
      return;
    }

    this.salvando.set(true);

    try {
      const prof = this.profissional;
      const dados: Partial<IProfissional> = {
        nome: this.form.value.nome,
        descricao: this.form.value.descricao || undefined,
        perfilAcesso: this.form.value.perfilAcesso || undefined,
        // form.value exclui controles desabilitados — para PROFISSIONAL o
        // controle fica disable()d, então lemos direto para não perder o
        // valor forçado (true) no payload.
        prestaAtendimento: this.form.get('prestaAtendimento')?.value,
        ferias: this.form.value.ferias,
        inicioExpediente: this.normalizarHorario(
          this.form.value.inicioExpediente
        ),
        fimExpediente: this.normalizarHorario(this.form.value.fimExpediente),
        almocoInicio:
          this.normalizarHorario(this.form.value.almocoInicio) || undefined,
        almocoFim:
          this.normalizarHorario(this.form.value.almocoFim) || undefined,
        estabelecimentoId,
        status: true,
        diasSemanaPresente: this.form.value.diasSelecionados.map(
          (diaId: number | string) =>
            ({
              ...(prof?.id ? { profissionalId: prof.id } : {}),
              diaId: Number(diaId),
            } as unknown as IProfissionalDiasPresente)
        ),
        servicos: (this.form.value.servicosSelecionados ?? []).map(
          (servicoId: number | string) =>
            ({
              servicoId: String(servicoId),
            } as unknown as IProfissionalServico)
        ),
      };

      if (prof) {
        await this.profissionalService.atualizar(prof.id, dados);
        this.closed.emit();
      } else {
        const novoId = await this.profissionalService.criar(dados);
        this.profissional = { ...dados, id: novoId, pessoaId: null } as IProfissional;
        this.profissionalRecemCriado.set(true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      this.salvando.set(false);
    }
  }

  concluirCriacao() {
    this.closed.emit();
  }

  private normalizarHorario(horario?: string | null): string {
    if (!horario) {
      return '';
    }

    const partes = horario.split(':');
    const hora = partes[0]?.padStart(2, '0') ?? '00';
    const minuto = partes[1]?.padStart(2, '0') ?? '00';
    return `${hora}:${minuto}`;
  }

  private isAlmocoValido(
    inicioExpediente: string,
    fimExpediente: string,
    almocoInicio?: string,
    almocoFim?: string
  ): boolean {
    if (!almocoInicio && !almocoFim) {
      return true;
    }

    if (!almocoInicio || !almocoFim) {
      return false;
    }

    const inicioExpedienteMin = this.parseHorario(inicioExpediente);
    const fimExpedienteMin = this.parseHorario(fimExpediente);
    const almocoInicioMin = this.parseHorario(almocoInicio);
    const almocoFimMin = this.parseHorario(almocoFim);

    if (almocoFimMin <= almocoInicioMin) {
      return false;
    }

    return (
      almocoInicioMin >= inicioExpedienteMin && almocoFimMin <= fimExpedienteMin
    );
  }
}
